// Researcher (F02, ADR-0016): findings from primary sources — leads (one reader) and verified (a second reader confirmed the
// excerpt and effective date blind: the view hides the first reading from everyone but its reader) — and proposals with the
// engine's plain-language explanation, what they touch and which open engagements they reach. The partner approves or rejects
// a proposal (research.decide) after reading the explanation; a disputed finding is settled by a partner (research.settle).
import { useState } from "react";
import { useApp } from "../../app/context";
import { Card, PageHeader, StatusPill, TabPanel, Tabs } from "../../components";
import type { ResearchFinding } from "../../data/types";
import { fmtDate, fmtStamp } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { ActButton, ApprovalPanel, Computed, ModelGate, useActs, useFirmModels } from "./common";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export function ResearcherPage() {
  const { ds, me } = useApp();
  const ms = useFirmModels(["research"]);
  const findings = useLoad(() => ds.researchFindings(), [ds]);
  const [tab, setTab] = useState("proposals");
  const [act, drawer] = useActs();
  const fs = findings.data ?? [];
  const leads = fs.filter((f) => f.status !== "verified");
  const props: Doc[] = (ms.data?.research?.doc as Doc | undefined)?.proposals ?? [];
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Researcher" subtitle="Changes in standards and rules from primary sources: leads, second-reader verification, and proposals the partner decides."
        actions={<ActButton command="research.submit" label="Submit a finding…" onClick={() => act({ command: "research.submit", title: "Submit a research finding" })} />} />
      <Tabs label="Research" idPrefix="rs" value={tab} onChange={setTab} tabs={[
        { id: "proposals", label: "Proposals", count: props.filter((p) => p.status === "proposed").length },
        { id: "leads", label: "Leads", count: leads.length }, { id: "verified", label: "Verified", count: fs.length - leads.length },
      ]} />
      <TabPanel id="proposals" value={tab} idPrefix="rs">
        {ms.error && <p role="alert" className="text-sm text-blocked">{ms.error}</p>}
        {ms.data && (
          <ModelGate<Doc> kind="research" m={ms.data.research} label="Research proposals">
            {(d, m) => (d.proposals ?? []).length === 0 ? <p className="text-sm text-muted">No proposal. A proposal is made from a verified finding (research.propose).</p> : (
              <div className="space-y-3">
                <div className="flex justify-end"><Computed m={m} /></div>
                {(d.proposals as Doc[]).map((p) => (
                  <Card key={p.id} title={<span className="flex flex-wrap items-center gap-2"><StatusPill state={p.status} /><span className="font-mono text-xs">RP-{String(p.id).padStart(5, "0")}</span>{p.what}</span>}>
                    <ApprovalPanel explanation={p.explanation} missing="No explanation published for this proposal."
                      evidence={
                        <div className="space-y-2 text-sm">
                          <blockquote className="rounded border border-line bg-sunken px-3 py-2">“{p.excerpt}” <span className="block text-xs text-muted">quoted from the source as data · <a className="text-brand hover:underline" href={p.source_url} target="_blank" rel="noreferrer">{p.source_url}</a> · effective {fmtDate(p.effective_on)}</span></blockquote>
                          <p><span className="text-muted">Touches:</span> {(p.targets ?? []).map((t: Doc) => `${String(t.kind).replace(/_/g, " ")} ${t.id}${t.flag ? ` (${t.flag})` : ""}`).join("; ")}</p>
                          <p><span className="text-muted">Open engagements it reaches:</span> {(p.engagements ?? []).map((e: Doc) => `${e.engagement} — ${e.why}`).join("; ") || "none"}</p>
                          <p className="text-xs text-muted">Proposed by {p.by}{p.agent ? ` through ${p.agent}` : ""}{p.decided_by ? ` · decided by ${p.decided_by}` : ""}</p>
                        </div>
                      }
                      decisions={p.status === "proposed" ? (
                        <>
                          <ActButton command="research.decide" label="Approve…" tone="primary" onClick={() => act({ command: "research.decide", fixed: { id: p.id, decision: "approve" }, title: `Approve RP-${String(p.id).padStart(5, "0")}` })} />
                          <ActButton command="research.decide" label="Reject…" tone="danger" onClick={() => act({ command: "research.decide", fixed: { id: p.id, decision: "reject" }, title: `Reject RP-${String(p.id).padStart(5, "0")}` })} />
                          {me.role !== "partner" && <span className="text-xs text-muted">Only a partner decides a research proposal.</span>}
                        </>
                      ) : <span className="text-sm text-muted">Decided{p.decided_by ? ` by ${p.decided_by}` : ""}: {p.status}.</span>} />
                  </Card>
                ))}
              </div>
            )}
          </ModelGate>
        )}
      </TabPanel>
      <TabPanel id="leads" value={tab} idPrefix="rs"><Findings rows={leads} loading={findings.loading} error={findings.error} act={act} empty="No lead waiting for a second reader." /></TabPanel>
      <TabPanel id="verified" value={tab} idPrefix="rs"><Findings rows={fs.filter((f) => f.status === "verified")} loading={findings.loading} error={findings.error} act={act} empty="No verified finding yet." /></TabPanel>
      {drawer}
    </div>
  );
}

function Findings({ rows, loading, error, act, empty }: { rows: ResearchFinding[]; loading: boolean; error?: string; act: ReturnType<typeof useActs>[0]; empty: string }) {
  const { me } = useApp();
  if (error) return <p role="alert" className="text-sm text-blocked">{error}</p>;
  if (loading && !rows.length) return <p className="text-sm text-muted">Loading findings…</p>;
  if (!rows.length) return <p className="text-sm text-muted">{empty}</p>;
  return (
    <ul className="space-y-3">
      {rows.map((f) => (
        <li key={f.id}>
          <Card title={<span className="flex flex-wrap items-center gap-2"><StatusPill state={f.status === "read" ? "waiting" : f.status} label={f.status === "read" ? "Lead — one reader" : undefined} />{f.title}</span>}>
            <div className="space-y-2 text-sm">
              <p className="text-xs text-muted">{f.source} · {f.tier ?? "tier —"} · published {fmtDate(f.publishedOn)} · first read by {f.firstReader ?? "—"}{f.agent ? ` (${f.agent})` : ""} {fmtStamp(f.at)}</p>
              {f.url && <a className="break-all text-xs text-brand hover:underline" href={f.url} target="_blank" rel="noreferrer">{f.url}</a>}
              {f.blind ? (
                <p className="rounded-md border border-agent-line bg-agent-bg px-3 py-2 text-agent">The first reading is hidden until a second reader confirms it: read the source yourself and record what it says.</p>
              ) : (
                <>
                  {f.whatChanged && <p><span className="text-muted">What changed:</span> {f.whatChanged}</p>}
                  {f.excerpt && <blockquote className="rounded border border-line bg-sunken px-3 py-2">“{f.excerpt}” <span className="block text-xs text-muted">effective {fmtDate(f.effectiveOn)}</span></blockquote>}
                </>
              )}
              {f.verifiedBy && <p className="text-xs">Confirmed by {f.verifiedBy} on {fmtStamp(f.verifiedAt)}.</p>}
              <div className="flex flex-wrap gap-2">
                {f.status === "read" && f.firstReader !== me.personName && <ActButton command="research.confirm" label="Confirm as second reader…" tone="primary" onClick={() => act({ command: "research.confirm", fixed: { id: f.id }, title: `Second reading of finding ${f.id}` })} />}
                {f.status === "disputed" && <ActButton command="research.settle" label="Settle…" onClick={() => act({ command: "research.settle", fixed: { id: f.id }, title: `Settle finding ${f.id}` })} />}
                {f.status === "verified" && <ActButton command="research.propose" label="Propose a change…" onClick={() => act({ command: "research.propose", fixed: { finding: f.id }, title: `Propose a change from finding ${f.id}` })} />}
              </div>
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}
